import classNames from "classnames";
import * as React from "react";

import {
  UserIcon,
  DeviceIcon,
  InvocationIcon,
  IntegrationIcon,
  OrgIcon,
  UnknownIcon,
} from "@/shared/components/icons";
import { Goto } from "@/shared/navigation";
import { Principal } from "@/shared/principal";

import { RobotoLinkHoverUnderline } from "../RobotoLinkHoverUnderline";

import styles from "./RobotoPrincipalUnderlineLink.module.css";

interface RobotoPrincipalUnderlineLinkProps {
  principal: Principal | string;
  icon?: boolean;
  className?: classNames.Argument;
}

type IconName =
  | "user"
  | "org"
  | "device"
  | "invocation"
  | "integration"
  | "unknown";

const linkTextAndLinkToFromPrincipal = (
  principal: Principal | string,
): {
  linkText: string;
  linkTo: string;
  iconName: IconName;
} => {
  let principalAsObj: Principal;

  if (typeof principal === "string") {
    const principalFromStr = Principal.fromString(principal).ok;
    if (!principalFromStr) {
      return {
        linkTo: "#",
        linkText: principal,
        iconName: "unknown",
      };
    } else {
      principalAsObj = principalFromStr;
    }
  } else {
    principalAsObj = principal;
  }

  let linkTo = "#";

  switch (principalAsObj.ptype) {
    case "device":
      return {
        linkTo: `/devices/${encodeURIComponent(principalAsObj.unqualifiedId)}`,
        linkText: principalAsObj.unqualifiedId,
        iconName: "device",
      };
    case "integration":
      if (principalAsObj.id === "foxglove-import") {
        linkTo = Goto.route.FoxgloveIntegration;
      }

      return {
        linkTo: linkTo,
        linkText: principalAsObj.id,
        iconName: "integration",
      };
    case "invocation":
      return {
        linkTo: `/invocations/${encodeURIComponent(principalAsObj.id)}`,
        linkText: principalAsObj.id,
        iconName: "invocation",
      };
    case "user":
      return {
        linkTo: `/users/${encodeURIComponent(principalAsObj.id)}`,
        linkText: principalAsObj.id,
        iconName: "user",
      };
    case "org":
      // No standalone org page exists. Admins manage other orgs from
      // /admin?tab=1 (System users only), so a regular user clicking
      // would land on an unauthorized page. Render the id without a
      // link target.
      return {
        linkTo: "#",
        linkText: principalAsObj.id,
        iconName: "org",
      };
    default:
      return {
        linkTo: "#",
        linkText: principalAsObj.id,
        iconName: "unknown",
      };
  }
};

const iconFromIconName = (iconName: IconName) => {
  switch (iconName) {
    case "user":
      return <UserIcon fontSize="small" color="primary" />;
    case "org":
      return <OrgIcon fontSize="small" color="primary" />;
    case "device":
      return <DeviceIcon fontSize="small" color="primary" />;
    case "invocation":
      return <InvocationIcon fontSize="small" color="primary" />;
    case "integration":
      return <IntegrationIcon fontSize="small" color="primary" />;
    case "unknown":
      return <UnknownIcon fontSize="small" color="primary" />;
  }
};

export const RobotoPrincipalUnderlineLink: React.FC<
  RobotoPrincipalUnderlineLinkProps
> = ({ principal, icon = false, className }) => {
  const { linkTo, linkText, iconName } =
    linkTextAndLinkToFromPrincipal(principal);

  return (
    <div className={classNames(styles.container, className)}>
      {/*
       * An unparseable / unknown principal (e.g. a bare id or email that isn't
       * a "ptype:id" string) has no real type, so suppress the misleading
       * "unknown" icon — but keep the label as a link.
       */}
      {icon && iconName !== "unknown" && iconFromIconName(iconName)}
      <RobotoLinkHoverUnderline to={linkTo}>
        {linkText}
      </RobotoLinkHoverUnderline>
    </div>
  );
};
